import React from 'react';
import {View} from 'react-native';
import {makeStyles} from '@rneui/themed';

import type {BottleVariant} from '../../../components/rewards/CoffeeCard/bottleGeometry';
import BottleRow from '../../../components/rewards/CoffeeCard/BottleRow';
import Text from '../../../components/ui-controls/Text';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import theme from '../../../style/theme';
import {CoffeeCardOffersItem} from '../../../transport/types';
import {resolveBottleVariant, toBottleCount} from '../../../utils/coffeeCard';

type CoffeeCardProps = {
  offer: CoffeeCardOffersItem;
};

const CoffeeCard = ({offer}: CoffeeCardProps) => {
  const styles = useStyles();
  const target = toBottleCount(offer.coffeeCardStats.target_quantity);

  // DIS-1913: the API can send a negative purchase count, so both counts are
  // clamped before they size the bottle row or reach the progress text.
  const filledSlots = Math.min(toBottleCount(offer.coffeeCardStats.purchased_quantity), target);
  const remainingSlots = target - filledSlots;

  return (
    <View testID="coffee-card" style={styles.container}>
      <Progress
        filledSlots={filledSlots}
        remainingSlots={remainingSlots}
        variant={resolveBottleVariant(offer)}
      />
      <Text
        style={styles.progressText}>{`You've bought ${filledSlots} out of ${target} drinks`}</Text>
    </View>
  );
};

type ProgressProps = {
  filledSlots: number;
  remainingSlots: number;
  variant: BottleVariant;
};

const Progress = ({filledSlots, remainingSlots, variant}: ProgressProps) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const styles = useStyles({isWebDesktopScreen});

  const bottleHeight = isWebDesktopScreen ? 200 : 130;
  const fillColor = theme.colour.general.primary;

  // The bottles pour on every mount, so revisiting the offer replays the fill.
  // BottleRow holds no "already seen" state and this screen supplies none.
  return (
    <View testID="coffee-card-progress" style={styles.progressContainer}>
      <BottleRow
        filledCount={filledSlots}
        emptyCount={remainingSlots}
        height={bottleHeight}
        color={fillColor}
        variant={variant}
      />
    </View>
  );
};

const useStyles = makeStyles((_theme, {isWebDesktopScreen}: {isWebDesktopScreen?: boolean}) => {
  const spaceMultiplier = isWebDesktopScreen ? 2 : 1;
  return {
    container: {
      flexDirection: 'column',
      marginTop: 8 * spaceMultiplier,
      marginBottom: 16 * spaceMultiplier,
    },
    progressText: {
      textAlign: 'center',
    },
    progressContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'center',
      flexWrap: 'nowrap',
      gap: 8,
      marginBottom: 8 * spaceMultiplier,
      minHeight: isWebDesktopScreen ? 200 : 130,
    },
  };
});

export default CoffeeCard;
